import { Platform } from "react-native";

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

const store = new Map<string, CacheEntry<unknown>>();

const DEFAULT_TTL = 5 * 60 * 1000;
const STORAGE_KEY = "api-cache-v1";
const PERSIST_DEBOUNCE_MS = 500;

const isWeb = Platform.OS === "web";

let _asyncStorage: {
  getItem: (k: string) => Promise<string | null>;
  setItem: (k: string, v: string) => Promise<void>;
} | null = null;
let _asyncStorageFailed = false;

function getAsyncStorage() {
  if (_asyncStorageFailed) return null;
  if (_asyncStorage) return _asyncStorage;
  try {
    _asyncStorage = require("@react-native-async-storage/async-storage").default;
    return _asyncStorage;
  } catch {
    _asyncStorageFailed = true;
    return null;
  }
}

async function readPersisted(): Promise<string | null> {
  if (isWeb) {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }
  try {
    return (await getAsyncStorage()?.getItem(STORAGE_KEY)) ?? null;
  } catch {
    return null;
  }
}

async function writePersisted(json: string): Promise<void> {
  if (isWeb) {
    try {
      localStorage.setItem(STORAGE_KEY, json);
    } catch {
      /* best-effort persistence */
    }
    return;
  }
  try {
    await getAsyncStorage()?.setItem(STORAGE_KEY, json);
  } catch {
    /* best-effort persistence */
  }
}

let persistTimer: ReturnType<typeof setTimeout> | null = null;

function schedulePersist(): void {
  if (persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    writePersisted(JSON.stringify(Array.from(store.entries())));
  }, PERSIST_DEBOUNCE_MS);
}

let hydrated = false;
let hydratePromise: Promise<void> | null = null;

// Loads the persisted cache from disk into the in-memory store so the first
// render after a cold start can paint from last session's data instead of a
// blank/spinner state. Must be awaited (or `isCacheHydrated()` checked)
// before screens read the cache, since disk reads are async.
export function hydrateCache(): Promise<void> {
  if (hydratePromise) return hydratePromise;
  hydratePromise = (async () => {
    try {
      const json = await readPersisted();
      if (json) {
        const entries = JSON.parse(json) as [string, CacheEntry<unknown>][];
        for (const [key, entry] of entries) {
          if (!store.has(key)) store.set(key, entry);
        }
      }
    } catch {
      /* corrupt or unavailable — start with whatever is already in memory */
    } finally {
      hydrated = true;
    }
  })();
  return hydratePromise;
}

export function isCacheHydrated(): boolean {
  return hydrated;
}

export function cacheGet<T>(key: string): { data: T; stale: boolean } | null {
  const entry = store.get(key);
  if (!entry) return null;
  const age = Date.now() - entry.timestamp;
  return { data: entry.data as T, stale: age > entry.ttl };
}

export function cacheSet<T>(key: string, data: T, ttl = DEFAULT_TTL): void {
  store.set(key, { data, timestamp: Date.now(), ttl });
  schedulePersist();
}

export function cacheInvalidate(key: string): void {
  store.delete(key);
  schedulePersist();
}

export function cacheInvalidatePattern(prefix: string): void {
  for (const key of store.keys()) {
    if (key.startsWith(prefix)) store.delete(key);
  }
  schedulePersist();
}

export function cacheClear(): void {
  store.clear();
  schedulePersist();
}
